/* eslint-disable react-refresh/only-export-components */
import { lazy } from "react";
import { RouteObject, Navigate } from "react-router-dom";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import CorporatePage from "../pages/corporate/page";

const HomePage = lazy(() => import("../pages/home/page"));
const HowItWorksPage = lazy(() => import("../pages/how-it-works/page"));
const CommitmentsPage = lazy(
  () => import("../pages/how-it-works/engagements/page"),
);
const CountriesCoveragePage = lazy(
  () => import("../pages/how-it-works/countries/page"),
);
const HomesCoveragePage = lazy(
  () => import("../pages/how-it-works/logements/page"),
);
const DestinationsPage = lazy(() => import("../pages/destinations/page"));
const DestinationDetailPage = lazy(
  () => import("../pages/destination-detail/page"),
);
const MembersMapPage = lazy(() => import("../pages/members-map/page"));
const MemberProfilePage = lazy(() => import("../pages/member-profile/page"));
const RoomDetailPage = lazy(() => import("../pages/room-detail/page"));

const MessagingPage = lazy(() => import("../pages/messaging/page"));
const MyCalendarPage = lazy(() => import("../pages/my-calendar/page"));
const IncomingRequestsPage = lazy(
  () => import("../pages/incoming-requests/page"),
);
// const PricingPage = lazy(() => import('../pages/pricing/page'));
const BecomeHostPage = lazy(() => import("../pages/become-host/page"));
const HostExchangePage = lazy(() => import("../pages/host-exchanges/page"));
const TermsPage = lazy(() => import("../pages/terms/page"));
const PrivacyPage = lazy(() => import("../pages/privacy/page"));
const CommunityPage = lazy(() => import("../pages/community/page"));
const BlogPage = lazy(() => import("../pages/blog/page"));
const HelpPage = lazy(() => import("../pages/help/page"));
const CareersPage = lazy(() => import("../pages/careers/page"));
const ContactPage = lazy(() => import("../pages/contact/page"));
const NotFoundPage = lazy(() => import("../pages/NotFound"));

// Dashboard
const DashboardLayout = lazy(() => import("../layouts/DashboardLayout"));
const DashboardPage = lazy(() => import("../pages/dashboard/page"));
const CreateListingWizard = lazy(
  () => import("../pages/dashboard/listings/CreateListingWizard"),
);
const MyListingsPage = lazy(() => import("../pages/dashboard/listings/page"));
const ProfilePage = lazy(() => import("../pages/dashboard/profile/page"));
const PreferencesPage = lazy(
  () => import("../pages/dashboard/preferences/page"),
);
const WhereToGoPage = lazy(() => import("../pages/dashboard/where-to-go/page"));
const MyRequestPage = lazy(() => import("../pages/dashboard/my-request/page"));
const InboxPage = lazy(() => import("../pages/dashboard/messages/page"));
const CalendarPage = lazy(() => import("../pages/dashboard/calendar/page"));
const FavoritesPage = lazy(() => import("../pages/dashboard/favorites/page"));
const VaultPage = lazy(() => import("../pages/dashboard/vault/page"));

// Pages auth
const AuthPage = lazy(() => import("../pages/auth/page"));
const AuthCallback = lazy(() => import("../pages/auth/callback"));
const PaymentPage = lazy(() => import("../pages/payment/page"));

// Helper pour protéger une route
function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

const routes: RouteObject[] = [
  // Pages publiques
  {
    path: "/",
    element: <HomePage />,
  },
  {
    path: "/how-it-works",
    element: <HowItWorksPage />,
  },
  {
    path: "/how-it-works/engagements",
    element: <CommitmentsPage />,
  },
  {
    path: "/how-it-works/countries",
    element: <CountriesCoveragePage />,
  },
  {
    path: "/how-it-works/logements",
    element: <HomesCoveragePage />,
  },
  {
    path: "/corporate",
    element: <CorporatePage />,
  },
  {
    path: "/community",
    element: <CommunityPage />,
  },
  {
    path: "/blog",
    element: <Navigate to="/" replace />, // <BlogPage />,
  },
  {
    path: "/help",
    element: <HelpPage />,
  },
  {
    path: "/careers",
    element: <CareersPage />,
  },
  {
    path: "/contact",
    element: <ContactPage />,
  },
  {
    path: "/pricing",
    element: <Navigate to="/become-host" replace />,
  },
  {
    path: "/become-host",
    element: <BecomeHostPage />,
  },
  {
    path: "/host-exchanges",
    element: <HostExchangePage />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  // Auth
  {
    path: "/auth",
    element: <AuthPage />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    path: "/payment",
    element: (
      <Protected>
        <PaymentPage />
      </Protected>
    ),
  },
  // Pages de découverte (publiques — pas besoin de connexion)
  {
    path: "/destinations",
    element: (
      <Protected>
        <Navigate to="/dashboard/destinations" replace />
      </Protected>
    ),
  },
  {
    path: "/destinations/:slug",
    element: (
      <Protected>
        <DestinationDetailPage />
      </Protected>
    ),
  },
  {
    path: "/explore",
    element: <Navigate to="/members-map" replace />,
  },
  {
    path: "/members-map",
    element: (
      <Protected>
        <Navigate to="/dashboard/members-map" replace />
      </Protected>
    ),
  },
  {
    path: "/member/:id",
    element: <MemberProfilePage />,
  },
  {
    path: "/rooms/:id",
    element: <RoomDetailPage />,
  },
  // Redirects and Shortcuts
  {
    path: "/about",
    element: <Navigate to="/corporate" replace />,
  },
  {
    path: "/exchange",
    element: <Navigate to="/host-exchanges" replace />,
  },
  {
    path: "/membership",
    element: <Navigate to="/become-host" replace />,
  },
  {
    path: "/account",
    element: <Navigate to="/dashboard/account" replace />,
  },
  // Pages protégées (connexion requise)
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute requireSubscription={true}>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: "listings/new",
        element: <CreateListingWizard />,
      },
      {
        path: "listings",
        element: <MyListingsPage />,
      },
      {
        path: "account",
        element: <ProfilePage />,
      },
      {
        path: "preferences",
        element: <PreferencesPage />,
      },
      {
        path: "where-to-go",
        element: <WhereToGoPage />,
      },
      {
        path: "my-request",
        element: <MyRequestPage />,
      },
      {
        path: "destinations",
        element: <DestinationsPage />,
      },
      {
        path: "members-map",
        element: <MembersMapPage />,
      },
      {
        path: "incoming-requests",
        element: <IncomingRequestsPage />,
      },
      {
        path: "messages",
        element: <InboxPage />,
      },
      {
        path: "calendar",
        element: <CalendarPage />,
      },
      {
        path: "favorites",
        element: <FavoritesPage />,
      },
      {
        path: "vault",
        element: <VaultPage />,
      },
      // Future routes:
      // { path: 'listings', element: <MyListingsPage /> },
      // { path: 'listings/new', element: <CreateListingPage /> },
    ],
  },
  {
    path: "/favorites",
    element: (
      <Protected>
        <FavoritesPage />
      </Protected>
    ),
  },
  {
    path: "/messaging",
    element: (
      <Protected>
        <MessagingPage />
      </Protected>
    ),
  },
  {
    path: "/my-calendar",
    element: (
      <Protected>
        <MyCalendarPage />
      </Protected>
    ),
  },
  {
    path: "/incoming-requests",
    element: <Navigate to="/dashboard/incoming-requests" replace />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

export default routes;
